import { Link } from "@tanstack/react-router";
import { Lightbulb, ArrowRight, MapPin } from "lucide-react";

/**
 * Per-area "useful MOT & car care advice" module. Sits on the priority area
 * pages (Poplar, Canary Wharf, Isle of Dogs, Limehouse, Docklands, etc.) to
 * differentiate them from the templated boilerplate. Each entry is hand-written
 * so we don't trip the "thin local pages" pattern Google penalises.
 */

export interface AreaAdvice {
  /** Short eyebrow displayed above the H2. */
  heading: string;
  /** 2-3 short paragraphs of local context. */
  paragraphs: string[];
  /** Three "what tends to fail round here" tips. */
  tips: { title: string; body: string }[];
  /** Suggested guide(s) to read next. */
  guides: { slug: string; label: string }[];
}

export const AREA_ADVICE: Record<string, AreaAdvice> = {
  poplar: {
    heading: "MOT & car care advice for Poplar drivers",
    paragraphs: [
      "Most Poplar cars we see live a stop-start E14 life: short trips to Crisp Street, runs to the school gates, the odd longer drive on the A12. That pattern is hard on a few specific parts — brakes, batteries and DPFs on diesels — and tends to mask faults that only show on a longer run.",
      "If your car rarely gets above 40mph for more than 10 minutes at a time, your battery doesn't fully recharge, your DPF doesn't get hot enough to regenerate, and your brake discs corrode overnight. The fixes are simple if you spot them early.",
    ],
    tips: [
      { title: "Test your battery every autumn", body: "Cold mornings expose weak batteries. A 5-minute test is free; a roadside jump-start at 8am isn't." },
      { title: "Don't ignore overnight brake squeal", body: "If the noise clears within the first minute it's surface rust — common in E14. If it stays, book a brake check." },
      { title: "Diesel? Plan a 20-minute motorway run monthly", body: "Lets the DPF regenerate properly and prevents a £600+ forced regen at the garage." },
    ],
    guides: [
      { slug: "mot-warning-lights-before-test", label: "Warning lights before MOT" },
      { slug: "brake-noise-squeaking-grinding", label: "Why brakes squeak or grind" },
    ],
  },
  "canary-wharf": {
    heading: "MOT & car care advice for Canary Wharf drivers",
    paragraphs: [
      "Wharf cars tend to be newer, often leased, and frequently driven hard between home, the office and the gym. The most common issues we see are tyre wear from speed-bump-heavy estates, low-profile rims kerbed in tight bays, and DPF / EGR warnings on diesels that mostly do short runs.",
      "Premium and executive cars (BMW, Mercedes, Audi, Tesla) don't need dealer servicing to keep their warranty — independent servicing with the right parts protects the manufacturer warranty under the EU Block Exemption Regulation.",
    ],
    tips: [
      { title: "Check tyre sidewalls every fortnight", body: "Underground car park columns and Wharf kerbs are tyre killers. A bulge today is a blowout next week." },
      { title: "Book MOTs early", body: "You can MOT a car up to 28 days before the expiry and keep the original date. Saves a last-minute scramble." },
      { title: "Don't trust the dealer-only myth", body: "We service premium brands daily, stamp the book, and use OE-spec parts. Your warranty stays intact." },
    ],
    guides: [
      { slug: "tyre-check-before-mot", label: "Tyre checks before MOT" },
      { slug: "engine-light-east-london", label: "Engine warning light — what to do" },
    ],
  },
  "isle-of-dogs": {
    heading: "MOT & car care advice for Isle of Dogs drivers",
    paragraphs: [
      "Drivers on the Island do a lot of low-speed driving — Marsh Wall, Manchester Road, the loop round Mudchute. That's hard on clutches (lots of biting-point holding) and on coolant systems in summer (stop-start traffic, no airflow).",
      "Older flats with allocated bays often mean the same patch of asphalt for years — overnight moisture trapped under the car corrodes brake discs and exhausts faster than parking out in the open. Easy to spot at MOT time if you know what to look for.",
    ],
    tips: [
      { title: "Listen for a slipping clutch", body: "If revs climb but speed doesn't on Westferry Road in 3rd, your clutch is on its way out. Cheaper to fix now than after it strands you." },
      { title: "Check coolant level monthly in summer", body: "Long idles in stop-start traffic boil weak coolant. A 30-second check on a cold engine could save the head gasket." },
      { title: "Spray underside before MOT", body: "Light hose-down of suspension and brake lines removes salty grit that can hide corrosion advisories." },
    ],
    guides: [
      { slug: "car-pulling-to-one-side", label: "Why your car pulls to one side" },
      { slug: "brake-noise-squeaking-grinding", label: "Brake noise diagnosis" },
    ],
  },
  limehouse: {
    heading: "MOT & car care advice for Limehouse drivers",
    paragraphs: [
      "Limehouse drivers mix tight residential streets with longer A13 / Commercial Road runs. The biggest things we catch at MOT time round here are tyre damage from potholes near Narrow Street, headlight aim drifting after a kerb knock, and exhaust corrosion on older diesels.",
      "Cars parked on-street near the canal and basin get more morning condensation than most — keep an eye on wiper blades, headlight bulbs and door seals. Cheap to replace, but they fail MOTs.",
    ],
    tips: [
      { title: "Pothole? Check tracking", body: "A solid pothole hit can knock alignment out by 2-3mm. You won't always feel it, but your tyres will be bald in 6 months." },
      { title: "Bulbs and screenwash before booking the MOT", body: "More than 1 in 5 fails are bulbs or low screenwash — both 5-minute fixes." },
      { title: "Diesel exhaust corrosion", body: "Centre and rear sections rot from the inside. Worth an inspection if your car's 8+ years old." },
    ],
    guides: [
      { slug: "tyre-check-before-mot", label: "Tyre checks before MOT" },
      { slug: "mot-warning-lights-before-test", label: "Warning lights before MOT" },
    ],
  },
  docklands: {
    heading: "MOT & car care advice for Docklands drivers",
    paragraphs: [
      "The Docklands area covers a wide stretch — Royal Docks, Silvertown, Canning Town, Custom House — and that means a lot of motorway-feeder driving on the A13 and Lower Lea Crossing. Higher average speeds are kinder to engines but harder on suspension, especially with the surface joints round Bow Roundabout.",
      "Salt off the river also accelerates corrosion on cars left out on the street year-round. A quick underbody jet-wash twice a winter pays for itself in MOT advisories you don't get.",
    ],
    tips: [
      { title: "Listen for suspension knocks", body: "Worn drop links and bushes are the most common Docklands MOT advisory we see — usually a clonk over speed bumps." },
      { title: "Refresh brake fluid every 2 years", body: "Hygroscopic — absorbs moisture and degrades silently. Cheap to do at major service, expensive when brakes start to feel spongy on the A13." },
      { title: "Check tyre pressures monthly", body: "Higher motorway speeds heat under-inflated tyres faster — biggest cause of blowouts at speed." },
    ],
    guides: [
      { slug: "car-pulling-to-one-side", label: "Why your car pulls to one side" },
      { slug: "tyre-check-before-mot", label: "Tyre checks before MOT" },
    ],
  },
  whitechapel: {
    heading: "MOT & car care advice for Whitechapel drivers",
    paragraphs: [
      "Whitechapel cars deal with heavy stop-start traffic on Whitechapel Road, the A11 and the back streets round the Royal London. Lots of low-speed brake use, lots of idle time, and not much chance to clear cobwebs out the engine on a longer run.",
      "Older cars round here often spend years not seeing a motorway — that's when DPFs clog and batteries quietly die. Catching the symptoms before the MOT saves a re-test fee.",
    ],
    tips: [
      { title: "Plan a longer drive monthly", body: "Even a 30-minute A11/A12 run lets the engine, DPF and battery do what they're designed for. Stops half the slow-decay problems." },
      { title: "Watch for an EML on diesels", body: "If your engine management light comes on in the week before MOT, get it scanned BEFORE the test — cheaper than a fail and retest." },
      { title: "Wipers and washers", body: "Free MOT fail to fix. Test them in the rain, not on the day of the test." },
    ],
    guides: [
      { slug: "engine-light-east-london", label: "Engine warning light — what to do" },
      { slug: "mot-warning-lights-before-test", label: "Warning lights before MOT" },
    ],
  },
  bow: {
    heading: "MOT & car care advice for Bow drivers",
    paragraphs: [
      "Bow drivers get the worst of both worlds — slow grinding around Bow Road and the roundabout, then bursts of higher-speed driving on the A11 and A12. That's a brake-and-tyre-heavy life, and we see plenty of cars in for advisories on both before MOT.",
      "Devons Road and Bow Common Lane have notoriously rough surfaces in places — suspension bushes and drop links wear out faster than average round here.",
    ],
    tips: [
      { title: "Brake pad inspection every 10k miles", body: "Or at any service — front pads on Bow stop-start driving rarely make 30,000 miles." },
      { title: "Suspension knock = book it", body: "A clonk over speed bumps is almost always a drop link or anti-roll bar bush. Cheap to fix, MOT-critical if ignored." },
      { title: "Tyre pressure check after pothole season", body: "Spring is the worst time for pothole damage to alloys and sidewalls. Two minutes with a gauge picks up slow punctures early." },
    ],
    guides: [
      { slug: "brake-noise-squeaking-grinding", label: "Brake noise diagnosis" },
      { slug: "car-pulling-to-one-side", label: "Car pulling to one side" },
    ],
  },
  "mile-end": {
    heading: "MOT & car care advice for Mile End drivers",
    paragraphs: [
      "Mile End is one of the most car-aware postcodes we serve — plenty of long-term residents, plenty of older cars kept running on a budget. The most common MOT advisories we see round here are corrosion on brake pipes, worn track-rod-end ball joints, and exhaust rust on cars 10+ years old.",
      "An honest pre-MOT check picks all three up before the test rather than after a fail. We don't push work that isn't needed — but corrosion advisories tend to become fails the following year.",
    ],
    tips: [
      { title: "Pre-MOT check is worth it on older cars", body: "We'll tell you straight what'll fail and what'll be advisory — much better than a surprise on the day." },
      { title: "Engine oil level monthly", body: "Older engines burn a small amount of oil between services. Run it dry and you'll need a £2,000+ rebuild instead of a £15 top-up." },
      { title: "Don't ignore knocking on bumps", body: "Track-rod-end play is a guaranteed MOT fail and a steering safety issue — easy fix if caught early." },
    ],
    guides: [
      { slug: "mot-warning-lights-before-test", label: "Warning lights before MOT" },
      { slug: "tyre-check-before-mot", label: "Tyre checks before MOT" },
    ],
  },
  stratford: {
    heading: "MOT & car care advice for Stratford drivers",
    paragraphs: [
      "Stratford drivers cover everything from short Westfield trips to longer commutes out on the A12 and beyond. Hybrid and EV ownership is higher here than the wider E14 average — both have specific service needs that differ from a petrol or diesel.",
      "Hybrid 12V auxiliary batteries fail more often than people expect (the small battery, not the big traction pack). EV brakes corrode faster than ICE brakes because regenerative braking means the friction discs are rarely used. We diagnose and service both at the workshop.",
    ],
    tips: [
      { title: "Hybrid 12V battery test annually", body: "Symptoms of failure look like a major fault on the dash. Usually it's a £100 battery and a 15-minute job." },
      { title: "EV? Ask for a brake clean & lubricate at service", body: "Stops disc corrosion and pad sticking — common on Teslas, Polestars and Hyundais." },
      { title: "Tyre check before motorway driving", body: "Higher speeds on the A12 heat up worn or under-inflated tyres fast — biggest cause of motorway blowouts." },
    ],
    guides: [
      { slug: "tyre-check-before-mot", label: "Tyre checks before MOT" },
      { slug: "engine-light-east-london", label: "Engine warning light — what to do" },
    ],
  },
  "bethnal-green": {
    heading: "MOT & car care advice for Bethnal Green drivers",
    paragraphs: [
      "Bethnal Green is mostly residential streets and short hops to Roman Road or up to Hackney. Cars round here often do under 8,000 miles a year — which sounds easy on the car, but low-mileage life has its own problems. Brake corrosion, perished tyres, flat batteries and DPF blockages on diesels are all more common than on high-mileage cars.",
      "If your car sits unused for more than a week at a time, give it a 20-minute drive once a fortnight. That's enough to keep the battery healthy, the brakes clean, and (on diesels) the DPF regenerated.",
    ],
    tips: [
      { title: "Drive it weekly, even if you don't need to", body: "Solves 80% of low-mileage problems before they cost you money." },
      { title: "Tyre age matters even with tread", body: "Tyres over 6 years old perish and crack regardless of mileage. The 4-digit date code is on the sidewall." },
      { title: "Pre-MOT inspection on cars 5+ years old", body: "Catches corrosion advisories before they become fails. Worth it on any older low-mileage car." },
    ],
    guides: [
      { slug: "tyre-check-before-mot", label: "Tyre checks before MOT" },
      { slug: "mot-warning-lights-before-test", label: "Warning lights before MOT" },
    ],
  },
};

interface Props {
  areaSlug: string;
  areaName: string;
}

export function AreaAdviceBlock({ areaSlug, areaName }: Props) {
  const advice = AREA_ADVICE[areaSlug];
  if (!advice) return null;
  return (
    <section className="py-14 md:py-20 bg-secondary/40">
      <div className="mx-auto max-w-4xl px-4">
        <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary inline-flex items-center gap-1.5">
          <MapPin className="h-3.5 w-3.5" /> Local advice
        </p>
        <h2 className="mt-3 text-2xl md:text-3xl font-bold text-foreground">{advice.heading}</h2>
        <div className="mt-4 space-y-3 text-[15px] md:text-base text-muted-foreground leading-relaxed max-w-3xl">
          {advice.paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        <div className="mt-7 grid grid-cols-1 md:grid-cols-3 gap-3">
          {advice.tips.map((t) => (
            <div key={t.title} className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-start gap-2">
                <Lightbulb className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <h3 className="text-sm font-bold text-foreground">{t.title}</h3>
              </div>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t.body}</p>
            </div>
          ))}
        </div>

        {advice.guides.length > 0 && (
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mr-1">
              Read next for {areaName}:
            </span>
            {advice.guides.map((g) => (
              <Link
                key={g.slug}
                to="/guide/$slug"
                params={{ slug: g.slug }}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-2 text-sm font-semibold text-foreground hover:border-primary/50"
              >
                {g.label}
                <ArrowRight className="h-3.5 w-3.5 text-primary" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
